import { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { CHURCH_ROLES, ROLE_LEVEL, roleName } from "@/lib/access";
import { fmtDate } from "@/lib/dates";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Search, Pencil, Trash2, Phone, Mail, Printer, Camera } from "lucide-react";
import { ImageCropDialog } from "@/components/ImageCropDialog";

const STATUS_STYLES = {
  active: "bg-[#EAF2E8] text-[#2D5A27] border-[#cfe3cb]",
  visitor: "bg-[#FEF3C7] text-[#B45309] border-[#f5e0a3]",
  inactive: "bg-[#F3EFEA] text-[#7A8A78] border-[#E5E0D8]",
};

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", family_name: "", status: "active", dob: "", join_date: "", address: "", notes: "", ministry_ids: [] };

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [rawImage, setRawImage] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const photoRef = useRef(null);

  const load = () => {
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/ministries").then((r) => setMinistries(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const ministryName = (id) => ministries.find((m) => m.id === id)?.name;
  const ministryOrder = (ids = []) => ministries.filter((min) => ids.includes(min.id)).map((min) => min.id);

  const filtered = useMemo(() => {
    return members
      .filter((m) => {
        const q = search.toLowerCase();
        const match = `${m.first_name} ${m.last_name} ${m.family_name} ${m.email}`.toLowerCase().includes(q);
        const statusOk = statusFilter === "all" || m.status === statusFilter;
        const roleOk = roleFilter === "all" || (m.church_role || "Member") === roleFilter;
        return match && statusOk && roleOk;
      })
      .sort((a, b) =>
        `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`, undefined, { sensitivity: "base" })
      );
  }, [members, search, statusFilter, roleFilter]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setPhotoFile(null); setPhotoPreview(null); setOpen(true); };
  const openEdit = (m) => {
    setForm({ ...EMPTY, ...m, ministry_ids: m.ministry_ids || [] });
    setEditId(m.id);
    setPhotoFile(null);
    setPhotoPreview(null);
    setOpen(true);
  };

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type)) {
      toast.error("Please upload a JPG, PNG, WEBP or GIF image"); return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setRawImage(reader.result); setCropOpen(true); };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const onCropped = (file) => {
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setCropOpen(false);
    setRawImage(null);
  };

  const save = async () => {
    if (!form.first_name || !form.last_name) { toast.error("First and last name are required"); return; }
    try {
      let mid = editId;
      if (editId) {
        await api.put(`/members/${editId}`, form);
      } else {
        const res = await api.post("/members", form);
        mid = res.data?.id;
      }
      if (photoFile && mid) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/members/${mid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editId ? "Member updated" : "Member added");
      setOpen(false);
      load();
    } catch { toast.error("Could not save member"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/members/${id}`); toast.success("Member removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const toggleMinistry = (id) => {
    setForm((f) => ({
      ...f,
      ministry_ids: f.ministry_ids.includes(id)
        ? f.ministry_ids.filter((x) => x !== id)
        : [...f.ministry_ids, id],
    }));
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A8A78]" />
            <Input data-testid="members-search" placeholder="Search members or families..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white h-11" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-11 bg-white" data-testid="members-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="visitor">Visitor</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40 h-11 bg-white" data-testid="members-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {CHURCH_ROLES.map((r) => (
                <SelectItem key={r} value={r} data-testid={`role-filter-${r.replace(" ", "-")}`}>
                  {roleName(r)} ({members.filter((m) => (m.church_role || "Member") === r).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-3">
          <Button asChild variant="outline" data-testid="print-directory-button" className="h-11 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
            <Link to="/print/directory"><Printer className="h-4 w-4 mr-2" /> Print Directory</Link>
          </Button>
          <Button data-testid="add-member-button" onClick={openNew} className="h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
            <UserPlus className="h-4 w-4 mr-2" /> Add Member
          </Button>
        </div>
      </div>

      <Card className="bg-white border border-[#E5E0D8] shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-[#E5E0D8]">
              <TableHead>Member</TableHead>
              <TableHead className="hidden md:table-cell">Family</TableHead>
              <TableHead className="hidden lg:table-cell">Contact</TableHead>
              <TableHead className="hidden lg:table-cell">Ministries</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((m) => (
              <TableRow key={m.id} className="border-[#E5E0D8]" data-testid={`member-row-${m.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full overflow-hidden flex items-center justify-center text-white text-sm font-semibold shrink-0"
                      style={{ backgroundColor: m.avatar_color || "#2D5A27" }}>
                      {m.photo_path
                        ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/members/${m.id}/photo?v=${m.photo_path}`} alt="" className="h-full w-full object-cover" />
                        : <>{m.first_name?.[0]}{m.last_name?.[0]}</>}
                    </div>
                    <div>
                      <p className="font-medium text-[#1C241B]">{m.first_name} {m.last_name}</p>
                      <p className="text-xs text-[#7A8A78]">Joined {fmtDate(m.join_date)}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-[#526050]">{m.family_name || "—"}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm text-[#526050]">
                  <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{m.phone || "—"}</div>
                  <div className="flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" />{m.email || "—"}</div>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {ministryOrder(m.ministry_ids).map((id) => (
                      <Badge key={id} variant="outline" data-testid={`member-${m.id}-ministry-${id}`} className="border-[#cfe3cb] bg-[#EAF2E8] text-[#2D5A27] font-normal">
                        {ministryName(id)}
                      </Badge>
                    ))}
                    {(!m.ministry_ids || m.ministry_ids.length === 0) && <span className="text-xs text-[#7A8A78]">None</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`capitalize font-medium ${STATUS_STYLES[m.status]}`}>{m.status}</Badge>
                  {m.church_role && m.church_role !== "Member" && (
                    <Badge variant="outline" className="ml-1 font-medium bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]" data-testid={`member-role-badge-${m.id}`}>{m.church_role}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`} className="h-8 w-8 text-[#526050]">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid={`delete-member-${m.id}`} className="h-8 w-8 text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#7A8A78]">No members found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editId ? "Edit Member" : "Add New Member"}</DialogTitle>
            <DialogDescription>Manage this member's details, ministries and profile photo.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-4 pt-2">
            <div className="h-16 w-16 rounded-full overflow-hidden flex items-center justify-center text-white text-lg font-semibold shrink-0" style={{ backgroundColor: form.avatar_color || "#2D5A27" }}>
              {photoPreview
                ? <img src={photoPreview} alt="" className="h-full w-full object-cover" />
                : (editId && form.photo_path
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/members/${editId}/photo?v=${form.photo_path}`} alt="" className="h-full w-full object-cover" data-testid="member-current-photo" />
                    : <>{form.first_name?.[0]}{form.last_name?.[0]}</>)}
            </div>
            <div>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="member-photo-input" onChange={pickPhoto} />
              <Button type="button" variant="outline" onClick={() => photoRef.current?.click()} data-testid="member-upload-photo-button" className="border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
                <Camera className="h-4 w-4 mr-2" /> {photoFile || form.photo_path ? "Change photo" : "Upload photo"}
              </Button>
              <p className="text-xs text-[#7A8A78] mt-1">JPG, PNG, WEBP or GIF · up to 5MB{editId ? "" : " · saved after you add the member"}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>First name</Label>
              <Input data-testid="member-firstname-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Last name</Label>
              <Input data-testid="member-lastname-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="member-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Family name</Label>
              <Input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} placeholder="The Miller Family" />
            </div>
            <div className="space-y-1.5">
              <Label>Church role</Label>
              <Select value={form.church_role || "Member"} onValueChange={(v) => setForm({ ...form, church_role: v })}
                disabled={user?.access !== "owner"}>
                <SelectTrigger data-testid="member-church-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHURCH_ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`church-role-option-${r.replace(" ", "-")}`}>{roleName(r)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#7A8A78]">{ROLE_LEVEL[form.church_role || "Member"]}{user?.access !== "owner" ? " · only the Pastor/owner can change" : ""}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="member-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="visitor">Visitor</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date of birth</Label>
              <Input type="date" data-testid="member-dob-input" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input type="date" data-testid="member-startdate-input" value={form.join_date || ""} onChange={(e) => setForm({ ...form, join_date: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Ministries</Label>
              <div className="flex flex-wrap gap-2">
                {ministries.map((min) => {
                  const on = form.ministry_ids.includes(min.id);
                  return (
                    <button type="button" key={min.id} onClick={() => toggleMinistry(min.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white text-[#526050] border-[#E5E0D8] hover:border-[#2D5A27]"}`}>
                      {min.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-member-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
              {editId ? "Save Changes" : "Add Member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImageCropDialog open={cropOpen} image={rawImage} onCancel={() => { setCropOpen(false); setRawImage(null); }} onCropped={onCropped} />
    </div>
  );
}
